<template>
  <div
    class="h-full w-full relative bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden transition-colors"
  >
    <VueFlow
      v-model:nodes="nodes"
      v-model:edges="edges"
      :key="blueprint.id"
      :default-viewport="{ zoom: 1 }"
      :max-zoom="4"
      :min-zoom="0.2"
      :node-types="nodeTypes"
      class="h-full w-full"
      :fit-view-on-init="fitViewOnInit"
      :nodes-draggable="true"
      :nodes-connectable="false"
      :elements-selectable="true"
      :delete-key-active="false"
      :pan-on-drag="true"
      :zoom-on-scroll="true"
    >
      <template #node-trigger="nodeProps">
        <TriggerNode v-bind="nodeProps" />
      </template>

      <template #node-step="nodeProps">
        <StepNode v-bind="nodeProps" />
      </template>

      <Background :gap="20" :pattern-color="'#aaa'" />
      <Controls />
    </VueFlow>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, ref, watch } from 'vue'
import { type NodeTypesObject, useVueFlow, VueFlow, MarkerType } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import TriggerNode from './nodes/TriggerNode.vue'
import StepNode from './nodes/StepNode.vue'
import type { WorkflowBlueprint, WorkflowStepConfig } from '@/types'

// Core imports from Vue Flow
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'

const props = withDefaults(
  defineProps<{
    blueprint: WorkflowBlueprint
    readOnly?: boolean
  }>(),
  {
    readOnly: true,
  },
)

const emit = defineEmits<{
  (e: 'select-step', stepName: string | null): void
}>()

const { setViewport, onNodeClick, onPaneClick, onNodeDragStop, onMoveEnd } = useVueFlow()

onNodeDragStop((event) => {
  if (!props.blueprint.id || props.readOnly) return
  const { node } = event
  const storageKey = `wf-pos-${props.blueprint.id}`
  const savedPositions = JSON.parse(localStorage.getItem(storageKey) || '{}')
  savedPositions[node.id] = node.position
  localStorage.setItem(storageKey, JSON.stringify(savedPositions))
})

onMoveEnd((event) => {
  if (!props.blueprint.id || props.readOnly) return
  const { flowTransform } = event
  const storageKey = `wf-vp-${props.blueprint.id}`
  localStorage.setItem(storageKey, JSON.stringify(flowTransform))
})

const fitViewOnInit = ref(true)

onMounted(async () => {
  if (props.blueprint.id) {
    const storageKey = `wf-vp-${props.blueprint.id}`
    const savedViewport = localStorage.getItem(storageKey)
    if (savedViewport) {
      try {
        const viewport = JSON.parse(savedViewport)
        if (viewport && typeof viewport.x === 'number' && !isNaN(viewport.x)) {
          fitViewOnInit.value = false
          await nextTick()
          setViewport(viewport)
        }
      } catch (e) {
        console.error('Failed to parse saved viewport', e)
      }
    }
  }
})

const nodeTypes = {
  trigger: TriggerNode,
  step: StepNode,
} as NodeTypesObject

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nodes = ref<any[]>([])
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const edges = ref<any[]>([])

// Mapping blueprint to Vue Flow elements
const mapBlueprintToElements = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newNodes: any[] = []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newEdges: any[] = []

  const storageKey = `wf-pos-${props.blueprint.id}`
  const savedPositions = props.blueprint.id
    ? JSON.parse(localStorage.getItem(storageKey) || '{}')
    : {}

  // Add Trigger Node
  if (props.blueprint.trigger) {
    newNodes.push({
      id: 'trigger-start',
      type: 'trigger',
      position: savedPositions['trigger-start'] || { x: 250, y: 50 },
      data: {
        type: props.blueprint.trigger.type,
        topic: props.blueprint.trigger.topic,
        objectType: props.blueprint.trigger.objectType,
      },
      selectable: true,
    })
  }

  // Add Edge from Trigger if nextStep is defined
  if (props.blueprint.trigger && props.blueprint.trigger.nextStep?.step) {
    const ns = props.blueprint.trigger.nextStep
    const targetId = ns.step === 'END' ? 'end-node' : `step-${ns.step}`
    // Only add if target exists or is END
    const targetExists =
      props.blueprint.trigger &&
      (props.blueprint.steps.some((s) => s.name === ns.step) || ns.step === 'END')

    if (targetExists) {
      const color =
        ns.color === 'success'
          ? '#22c55e'
          : ns.color === 'error' || ns.color === 'danger'
            ? '#ef4444'
            : '#3b82f6'
      const label = ns.label || (ns.condition && ns.condition !== 'default' ? ns.condition : '')

      newEdges.push({
        id: `e-trigger-next`,
        source: 'trigger-start',
        target: targetId,
        label,
        animated: true,
        type: props.blueprint.edgeType || 'default',
        markerEnd: MarkerType.ArrowClosed,
        style: { stroke: color, strokeWidth: 2 },
        labelStyle: { fill: '#fff', fontWeight: 700, fontSize: '10px' },
        labelBgStyle: { fill: color, fillOpacity: 0.8 },
        labelBgPadding: [4, 8],
        labelBgBorderRadius: 4,
      })
    }
  }

  // Add Step Nodes
  props.blueprint.steps.forEach((step, index) => {
    const isEndNode = step.stepType === 'EndStep' || step.name === 'END'
    const nodeId = isEndNode ? 'end-node' : `step-${step.name}`

    // Default position: top-down
    const defaultY = 150 * (index + 1)
    let position = savedPositions[nodeId]

    if (!position) {
      if (step.position && step.position.x !== 50) {
        // Migrate from blueprint to localStorage
        position = step.position
        savedPositions[nodeId] = position
      } else {
        position = { x: 250, y: defaultY }
      }
    }

    newNodes.push({
      id: nodeId,
      label: step.name,
      type: 'step',
      position: position,
      data: {
        ...step,
      },
      style: step.style || {},
      draggable: true,
    })
  })

  // Update localStorage if migration happened
  if (props.blueprint.id) {
    localStorage.setItem(storageKey, JSON.stringify(savedPositions))
  }

  // Add Edges
  props.blueprint.steps.forEach((step: WorkflowStepConfig) => {
    if (step.nextSteps && step.nextSteps.length > 0) {
      step.nextSteps.forEach((ns, edgeIndex) => {
        const targetExists =
          props.blueprint.steps.some((s) => s.name === ns.step) || ns.step === 'END'
        if (targetExists) {
          const isSystemStep = step.stepType === 'SystemStep'
          const defaultColor = isSystemStep ? '#64748b' : '#3b82f6'
          const color =
            ns.color === 'success'
              ? '#22c55e'
              : ns.color === 'error' || ns.color === 'danger'
                ? '#ef4444'
                : defaultColor
          const label = ns.label || (ns.condition && ns.condition !== 'default' ? ns.condition : '')

          newEdges.push({
            id: `e-${step.name}-${ns.step}-${edgeIndex}`,
            source: `step-${step.name}`,
            target: ns.step === 'END' ? 'end-node' : `step-${ns.step}`,
            label,
            animated: true,
            type: props.blueprint.edgeType || 'default',
            markerEnd: MarkerType.ArrowClosed,
            style: { stroke: color, strokeWidth: 2 },
            labelStyle: { fill: '#fff', fontWeight: 700, fontSize: '10px' },
            labelBgStyle: { fill: color, fillOpacity: 0.8 },
            labelBgPadding: [4, 8],
            labelBgBorderRadius: 4,
          })
        }
      })
    }
  })

  nodes.value = newNodes
  edges.value = newEdges
}

// Initial mapping
mapBlueprintToElements()

// Watch for external blueprint changes
watch(() => props.blueprint, mapBlueprintToElements, { deep: true })

onNodeClick((event) => {
  const { node } = event
  if (node.type === 'step') {
    emit('select-step', node.data.name)
  } else if (node.id === 'trigger-start') {
    emit('select-step', 'TRIGGER')
  } else {
    emit('select-step', null)
  }
})

onPaneClick(() => {
  emit('select-step', null)
})
</script>

<style>
.vue-flow__node-step.selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px #bfdbfe;
}
</style>
